<script>
    import {
        CardLayout,
        ConfirmActionModal,
        PageLoading,
        RichTextEditor,
    } from "./index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        addIcon,
        deleteIcon,
        editIcon,
        NoteTypes,
    } from "../../Utils/utilConstants.svelte";

    import {
        getFormatDateAndTime,
        showError,
        showInfo,
    } from "../../Utils/utilFunctions.svelte";

    import { user, allGroups } from "../../stores";
    import { cacheAllGroups } from "../../Utils/utilCache.svelte";

    import NotificationModal from "./NotificationModal.svelte";

    export let noteOwnerID,
        noteType,
        sectionTitle = "Notes",
        hasNotes = true,
        onFinishModifyNote = () => {},
        encounterDoctorGroupId = null;

    let isLoading = false,
        isNoteOpen = false,
        isConfirmActionModalOpen = false,
        isDeleteModalOpen = false,
        currentNote = {},
        confirmAction = () => {},
        confirmActionTitle = "",
        confirmActionPrompt = "",
        confirmButtonText,
        cancelButtonText,
        cancelAction,
        isActionRunning = false,
        noteContentInputEle,
        backUpNote = {},
        allNotes = [];

    const btnOptions = [
        {
            iFont: addIcon,
            iconFunction: () => addNote(),
            hasTooltip: false,
            btnText: "Add",
        },
    ];

    // let isUploadFileModalOpen = false;

    $: isUserDoctor = $user.role == 2;

    $: if (noteOwnerID && hasNotes) {
        loadNotes(noteOwnerID);
    }

    const loadNotes = async () => {
        if (!hasNotes) return;

        isLoading = true;

        await apiMethods.getMethod
            .getNotes(noteType, noteOwnerID)
            .then((res) => {
                allNotes = res.results;
            })
            .catch((err) => {
                showError(err);
            });

        isLoading = false;
    };

    const closeNoteDetails = () => {
        if (hasUnsavedChanges) showConfirmLeaveWithChangesPrompt();
        else isNoteOpen = false;
    };

    const showConfirmLeaveWithChangesPrompt = () => {
        confirmAction = () => {
            isConfirmActionModalOpen = false;
        };
        confirmActionTitle = "Changes not saved";
        confirmActionPrompt = "Discard changes and close?";
        cancelAction = discardChanges;
        cancelButtonText = "Discard Changes";
        confirmButtonText = "back";
        isConfirmActionModalOpen = true;
    };

    const discardChanges = () => {
        isConfirmActionModalOpen = false;
        isNoteOpen = false;
    };

    const proceedNote = async () => {
        if (currentNote.id) await updateNote();
        else await createNote();

        onFinishModifyNote();
    };

    const getNotifyDoctorDefault = () => {
        const userGroupDefault = $allGroups.find(g => g.id == $user.groupId)?.noteNotifyDoctorDefault ?? false;
        const doctorGroupDefault = encounterDoctorGroupId
            ? ($allGroups.find(g => g.id == encounterDoctorGroupId)?.noteNotifyDoctorDefault ?? false)
            : false;
        return userGroupDefault || doctorGroupDefault;
    };

    const addNote = async () => {
        if (showPushToDocToggle) {
            const forceRecache = true;
            await cacheAllGroups(forceRecache);
        }
        currentNote = {
            visibleToDoctors: getDefaultNoteVisibilityToDoctors(),
        };
        if (showPushToDocToggle) currentNote.notifyDoctor = getNotifyDoctorDefault();
        isNoteOpen = true;
    };

    const updateNote = async () => {
        if (!currentNote.id) return;
        isActionRunning = true;

        if (isUserDoctor)
            currentNote.visibleToDoctors = getNoteVisibilityToDoctors();

        await apiMethods.putMethod
            .updateNote(currentNote)
            .then((res) => {
                if (res.error) throw res.error;
                else {
                    showInfo("Note Updated");
                    loadNotes();
                    isNoteOpen = false;
                }
            })
            .catch((err) => {
                showError(err);
            });

        isActionRunning = false;
    };

    const getDefaultNoteVisibilityToDoctors = () => {
        if (isUserDoctor) return true;
        switch (noteType) {
            case NoteTypes.Account: // always hidden from Doctors
                return false;
            case NoteTypes.Patient:
            case NoteTypes.PatientEncounter:
            case NoteTypes.Estimate:
            case NoteTypes.Invoice:
            case NoteTypes.Receipt:
            case NoteTypes.AccountInvoice:
            case NoteTypes.TopUpInvoice:
            case NoteTypes.HealthFundInvoice:
            default:
                return true;
        }
    };

    const getNoteVisibilityToDoctors = () => {
        if (isUserDoctor) return true;
        switch (noteType) {
            case NoteTypes.Account: // always hidden from Doctors
                return false;
            case NoteTypes.Patient:
            case NoteTypes.PatientEncounter:
            case NoteTypes.Estimate:
            case NoteTypes.Invoice:
            case NoteTypes.Receipt:
            case NoteTypes.AccountInvoice:
            case NoteTypes.TopUpInvoice:
            case NoteTypes.HealthFundInvoice:
            default:
                return currentNote.visibleToDoctors;
        }
    };

    // show toggle when user is of high auth and note is not Account note;
    $: showVisibleToDoctorsToggle =
        $user.role >= 20 && noteType !== NoteTypes.Account;

    $: showPushToDocToggle = 
        $user.role >= 20 &&
        (noteType === NoteTypes.PatientEncounter ||
            noteType === NoteTypes.AccountCalendarEvent);

    $: enableEditThisNote = (note) => {
        if ($user.role >= 20) return true;
        if (isUserDoctor && $user.id === note.userId) return true;
        return false;
    };

    const updateNoteVisibility = (note) => {
        currentNote = note;
        updateNote();
    };

    const createNote = async () => {
        isActionRunning = true;

        const note = {
            relTo: noteType,
            relId: noteOwnerID,
            content: currentNote.content,
            visibleToDoctors: getNoteVisibilityToDoctors(),
            notifyDoctor: currentNote.notifyDoctor,
        };

        await apiMethods.postMethod
            .createNote(note)
            .then((res) => {
                if (res.error) throw res.error;
                else {
                    showInfo("Note Created");
                    loadNotes();
                    isNoteOpen = false;
                }
            })
            .catch((err) => {
                showError(err);
            });

        isActionRunning = false;
    };

    const askDeleteNote = (note) => {
        if (!note.id) return;
        currentNote = note;
        isDeleteModalOpen = true;
    };

    const deleteCurrentNote = async () => {
        if (currentNote.id) isActionRunning = true;

        await apiMethods.deleteMethod
            .deleteNote(currentNote.id)
            .then((res) => {
                if (res.error) throw res.error;
                else {
                    showInfo("Note Deleted");
                    isConfirmActionModalOpen = false;
                    isDeleteModalOpen = false;
                    loadNotes();
                    currentNote = {};
                }
            })
            .catch((err) => showError(err));

        isActionRunning = false;

        onFinishModifyNote();
    };

    const startEditNode = async (node) => {
        if (!node.id) return;
        if (showPushToDocToggle) {
            await cacheAllGroups(true);
        }
        currentNote = { ...node };
        if (showPushToDocToggle) currentNote.notifyDoctor = getNotifyDoctorDefault();
        isNoteOpen = true;
    };

    $: if (isNoteOpen && editorRef) onNoteOpen();

    const onNoteOpen = () => {
        setTimeout(() => {
            backUpNote = { ...currentNote };
            focusNoteInput();
        }, 0);
    };

    const focusNoteInput = () => {
        setTimeout(() => {
            if(editorRef.focus){
                editorRef?.focus();
            }
        }, 200);
    };

    const getNoteContent = (note) => {
        if(note.content){
            var content = note.content
                        .toString()
                        .replace(/&nbsp;/g, ' ')
                        .replace(/\u00A0/g, ' ');
            return content;
        }
        return "";
    }

    // $: hasUnsavedChanges =
    //     currentNote.content != backUpNote.content ||
    //     currentNote.visibleToDoctors != backUpNote.visibleToDoctors;
    $: hasUnsavedChanges =
        (currentNote.content ?? "") != (backUpNote.content ?? "") ||
        currentNote.visibleToDoctors != backUpNote.visibleToDoctors ||
        (currentNote.notifyDoctor ?? false) !=
            (backUpNote.notifyDoctor ?? false);

    let editorRef;
</script>

<CardLayout title={sectionTitle} {btnOptions}>
    <div class="flex flex-col flex-1 items-center gap-3 py-4 w-full">
        {#if isLoading}
            <PageLoading />
        {:else if allNotes.length}
            <div
                class="w-full grid grid-cols-12 font-semibold text-[14px] gap-x-3 table-item px-4 items-center"
            >
                <span class="col-span-6"> Note Content </span>
                <span class="col-span-2"> Added By </span>
                <span class="col-span-2"> Date </span>
                <span class="col-span-1">
                    {#if showVisibleToDoctorsToggle}
                        Visible To Doctors
                    {/if}
                </span>
                <span class="col-span-1 text-right pr-4">Actions</span>
            </div>

            {#each allNotes as note}
                <div
                    class="w-full grid grid-cols-12 gap-x-3 table-item pt-2 px-4 items-start"
                >
                    <div class="col-span-6 break-words whitespace-pre-wrap">
                        {@html getNoteContent(note)}
                    </div>

                    <span class="col-span-2">
                        {note.userName}
                    </span>

                    <span class="col-span-2">
                        {getFormatDateAndTime(note.createdDate)}
                    </span>

                    <span class="col-span-1">
                        {#if showVisibleToDoctorsToggle}
                            <input
                                type="checkbox"
                                class="toggle toggle-secondary"
                                bind:checked={note.visibleToDoctors}
                                on:change={() => updateNoteVisibility(note)}
                            />
                        {/if}
                    </span>

                    <span class="col-span-1 flex flex-row justify-end gap-x-2">
                        <!-- Hide edit btns in view account && relTo id not the same as logged in doctor -->
                        {#if enableEditThisNote(note)}
                            <button
                                class="btn btn-ghost btn-sm btn-circle"
                                on:click={() => startEditNode(note)}
                            >
                                <i class={editIcon}></i>
                            </button>
                            <button
                                class="btn btn-ghost btn-sm btn-circle"
                                on:click={() => askDeleteNote(note)}
                            >
                                <i class={deleteIcon}></i>
                            </button>
                        {/if}
                    </span>
                </div>
            {/each}
        {:else}
            <span class="col-span-12"> No Notes Yet </span>
        {/if}
    </div>
</CardLayout>

<NotificationModal
    bind:isModalOpen={isNoteOpen}
    title="Note Detail"
    cancelText="Cancel"
    closeModal={closeNoteDetails}
    closeModalFunction={closeNoteDetails}
    submitText={currentNote.id ? "Save" : "Create"}
    isConfirmDisabled={isActionRunning || !currentNote.content}
    isConfirming={isActionRunning}
    confirmAction={proceedNote}
    modalMaxWidth="max-w-6xl"
>
    <div class="flex flex-1 w-full gap-x-4 overflow-y-auto max-h-[500px]">
        <p class="title">Content:</p>
        <!-- <textarea
            class="textarea textarea-bordered w-full flex-1"
            bind:this={noteContentInputEle}
            rows="6"
            autocomplete="off"
            bind:value={currentNote.content}
        /> -->
        <div class="content">
            <RichTextEditor
                bind:content={currentNote.content}
                bind:this={editorRef}
            />
        </div>
        
    </div>
    {#if showVisibleToDoctorsToggle}
        <div class="flex flex-row gap-x-2 mt-4">
            <!-- hide this toggle if add account notes or user role is doctor -->
            <label class="title" for="visibleToDoc">Visible to Doctors:</label>
            <input
                type="checkbox"
                id="visibleToDoc"
                class="toggle toggle-secondary"
                bind:checked={currentNote.visibleToDoctors}
            />
        </div>
    {/if}
    {#if showPushToDocToggle}
        <div class="flex flex-row gap-x-2 mt-4">
            <!-- hide this toggle if add account notes or user role is doctor -->
            <label class="title" for="pushToDoc">Push notification to doctor:</label>
            <input
                type="checkbox"
                id="pushToDoc"
                class="toggle toggle-secondary"
                bind:checked={currentNote.notifyDoctor}
            />
        </div>
    {/if}
</NotificationModal>

<ConfirmActionModal
    bind:isConfirmActionModalOpen
    bind:isActionRunning
    on:confirmAction={confirmAction}
    on:cancelAction={cancelAction}
    title={confirmActionTitle}
    confirmPrompt={confirmActionPrompt}
    {confirmButtonText}
    {cancelButtonText}
/>

<NotificationModal
    bind:isModalOpen={isDeleteModalOpen}
    isConfirming={isActionRunning}
    closeModal={() => (isDeleteModalOpen = false)}
    closeModalFunction={() => (isDeleteModalOpen = false)}
    title="Delete Note"
    submitText="Delete"
    confirmButtonClass="btn-error"
    cancelButtonText="Cancel"
    cancelButtonClass="btn-accent"
    confirmAction={deleteCurrentNote}
>
    <p class="text-left">Delete Note?</p>
</NotificationModal>

<style>
    .table-item + .table-item {
        border-top: 2px solid #e5e7eb;
    }
    .title {
        width: 250px;
    }
    .content {
        width: calc(100% - 250px);
    }
</style>
